/**
 * Thin typed fetch wrapper.
 *
 * The token lives in localStorage; every call attaches it and a 401 clears
 * the session so the app falls back to the login screen rather than looping.
 */

const TOKEN_KEY = 'emailblast.token';

export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY);
export const setToken = (token: string): void => localStorage.setItem(TOKEN_KEY, token);
export const clearToken = (): void => localStorage.removeItem(TOKEN_KEY);

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details: unknown = null
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: { raw?: boolean } = {}
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getToken();
  if (token) headers.Authorization = 'Bearer ' + token;

  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  const response = await fetch(path, { method, headers, body: payload });

  if (response.status === 401) {
    clearToken();
    window.dispatchEvent(new CustomEvent('emailblast:unauthorized'));
  }

  if (!response.ok) {
    let code = 'HTTP_' + response.status;
    let message = response.statusText;
    let details: unknown = null;
    try {
      const json = (await response.json()) as { error?: { code: string; message: string; details: unknown } };
      if (json.error) {
        code = json.error.code;
        message = json.error.message;
        details = json.error.details;
      }
    } catch {
      // Non-JSON error body; the status text is all we have.
    }
    throw new ApiError(response.status, code, message, details);
  }

  if (options.raw) return (await response.text()) as unknown as T;
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

export function qs(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const str = search.toString();
  return str ? '?' + str : '';
}
